import { type SxProps, type Theme } from '@mui/material/styles';
import { type SystemStyleObject } from '@mui/system';

/**
 * Photographic backdrops — the one thing `patterns.ts` deliberately refuses to
 * do (it is paint, never an image), kept beside it because the two answer the
 * same question about a band and obey the same two rules: nothing reaches an
 * edge, and contrast is never negotiated.
 *
 * The site's car assets are cut-outs on a transparent background, so there is
 * no such thing as a full-bleed `cover` rendition of one: stretched, the car
 * distorts, and the empty region simply shows the band colour. A cut-out is
 * therefore always anchored to a bottom corner and sized from the picture,
 * never cropped to the band. This module paints that arrangement at two
 * strengths, and `emphasis` picks between them.
 *
 * `watermark` is the original and the default: grey, barely there, a texture
 * the eye reads as surface rather than as a car. The opacity is a variable
 * rather than a literal for the same reason the pattern inks are — a
 * near-white surface and a #242122 one carry the same image differently, so
 * the two values are stated once, here.
 *
 * `feature` is the louder one, for a band whose whole job is to be looked at.
 * The car keeps its colour and is drawn several times brighter, and the band
 * earns that back with a scrim: the layer's own colour, opaque under the text
 * column and opening to nothing over the corner the car sits in. Text floats
 * on the band colour it always had while the car reads as a photograph beside
 * it, which is the only way to raise a cut-out this far without spending the
 * contrast the copy needs (measured: 14:1 heading, 9:1 body — see the note on
 * `featureScrim`).
 */
export type SectionBackdropPlacement = 'left' | 'right';

/**
 * How loudly the car reads. `watermark` is safe on any tone; `feature` is
 * **only** legible on a dark band, because its scrim is built from the
 * near-black band colour and would paint a dark smear across a light surface.
 * That is why it is a named option rather than a number callers pass — the
 * same reason `WhatsAppButton` makes `outline` an emphasis and not a variant.
 */
export type SectionBackdropEmphasis = 'watermark' | 'feature';

type BackdropSx = Exclude<SxProps<Theme>, readonly unknown[]>;

const OPACITY_LIGHT = { '--kabex-backdrop-opacity': '0.11' };

/**
 * Lower, not matched. A light cut-out on a dark band is the high-contrast
 * direction of the pair (see the hero's note on dark cars), so the same number
 * would make the silhouette shout in dark mode while whispering in light.
 */
const OPACITY_DARK = { '--kabex-backdrop-opacity': '0.07' };

/**
 * A quarter-ellipse anchored on the same corner the car is. Solid where the
 * bumper sits, gone by roughly three-quarters of the way toward the band's
 * opposite edge and its top seam — so the silhouette never runs into the
 * section boundary and never arrives under the column of text.
 */
const cornerMask = (placement: SectionBackdropPlacement) =>
  `radial-gradient(125% 125% at ${placement === 'right' ? '100%' : '0%'} 100%, ` +
  '#000 0%, rgba(0, 0, 0, 0.7) 42%, transparent 76%)';

/**
 * Where the cut-out is anchored, once the band is wider than the layout.
 *
 * A backdrop is a full-bleed layer and the page's content is not: it lives in
 * a `Container maxWidth="lg"`, so past 1248px the band keeps growing and the
 * column of text stops. The car is anchored to the band's edge and capped at
 * its own `maxWidth`, which means that past some width its inner edge lifts
 * off the content entirely and it reads as a photograph stranded in the
 * margin rather than as a car parked beside the copy.
 *
 * That width is worth deriving rather than guessing. The car's inner edge sits
 * at `band - maxWidth`; the container's outer edge sits at `band / 2 + 600`.
 * They part company when `band / 2` exceeds `maxWidth + 600`, so the offset
 * that keeps them together is exactly `50% - (maxWidth + 600)` — and `max()`
 * with zero is what makes this free everywhere else. The expression is
 * negative at every width up to 2640px for the watermark and 3000px for the
 * feature, so no desktop, laptop, tablet or phone moves by a pixel; it only
 * engages on the ultrawide monitors where the problem is real, and it engages
 * continuously from zero, so there is no snap to catch the eye on a resize.
 */
const containerAnchor = (placement: SectionBackdropPlacement, maxWidth: number) => ({
  [placement]: `max(0px, calc(50% - ${maxWidth + 600}px))`,
});

/** How much of the car survives the band in `feature`. */
const FEATURE_OPACITY = 0.42;

/**
 * The band colour, as channels so the scrim can vary its alpha. Stated twice
 * because an `inverse` band is #0B0B0C in light mode and #1F1A1B in dark —
 * the same two values `Section` sets `bgcolor` from. A scrim built from the
 * wrong one would read as a panel laid over the band instead of as the band.
 */
const BAND_LIGHT = { '--kabex-band': '11, 11, 12' };
const BAND_DARK = { '--kabex-band': '31, 26, 27' };

/**
 * `cornerMask`'s inverse, and the reason `feature` can afford a car at 42%.
 * The mask fades the car out toward the text; this fades the *band* in over
 * it, opaque everywhere the copy can reach and clear only in the quarter
 * ellipse the car is anchored in.
 *
 * The stops are placed against where the text actually lands, not by eye. The
 * copy column is `maxWidth: 640` inside a `lg` container, so on a desktop its
 * far edge sits around 24% into this layer and roughly half its height — just
 * past the 52% stop, where the scrim is still ~0.92 opaque. Composited over a
 * white cut-out at 42% that leaves the heading at about 14:1 and the 0.78
 * body at about 9:1, both comfortably past AA. Widening the clear end is the
 * one change here that can spend that margin, so it is worth re-checking.
 */
const featureScrim = (placement: SectionBackdropPlacement) =>
  `radial-gradient(135% 135% at ${placement === 'right' ? '100%' : '0%'} 100%, ` +
  'rgba(var(--kabex-band), 0) 0%, rgba(var(--kabex-band), 0.18) 26%, ' +
  'rgba(var(--kabex-band), 0.72) 52%, rgb(var(--kabex-band)) 74%)';

/**
 * The loud arrangement. Two things separate it from the watermark beyond the
 * numbers: the opacity is on the `img` rather than on the box, so the `::after`
 * scrim stays fully opaque instead of fading with the car it is protecting;
 * and there is no `grayscale`, because at this strength a grey car reads as a
 * faded photograph rather than a deliberate one.
 */
const featureLayer = (placement: SectionBackdropPlacement) => ({
  ...BAND_LIGHT,
  position: 'absolute',
  insetBlock: 0,
  ...containerAnchor(placement, 900),
  // Wider than the watermark at every breakpoint: this one is meant to be
  // seen as a car, and a 46% slot reads as an ornament in the margin. Nearly
  // full width on a phone, where the band stacks and the car sits under the
  // foot of a single column.
  width: { xs: '92%', sm: '78%', md: '62%' },
  maxWidth: 900,
  pointerEvents: 'none',
  zIndex: 0,
  '& picture, & img': { display: 'block', width: '100%', height: '100%' },
  /**
   * The module's first rule, paid for here rather than assumed: `trim` leaves
   * the cut-out with no padding of its own, so a car anchored hard into the
   * corner puts its rear bumper exactly on the band's boundary and reads as a
   * crop instead of as a car. The watermark gets away with that at 7%; at 42%
   * it does not.
   *
   * The gap is on `picture` and not on the layer because the scrim is the
   * layer's `::after` at `inset: 0` — padding the layer would inset the scrim
   * too and leave an unprotected strip down the band's edge. A percentage and
   * not pixels so the inset keeps its proportion as the slot changes width.
   *
   * Only the side, never the bottom. The band's lower boundary is the ground
   * the car stands on, and lifting it off that line reads as a sticker
   * floating in the corner rather than as a car parked in the band.
   */
  '& picture': {
    boxSizing: 'border-box',
    [placement === 'right' ? 'paddingRight' : 'paddingLeft']: '5%',
  },
  // `contain`, for the same reason the watermark uses it: the car is already
  // trimmed to its own bounds, so it is never cropped or stretched.
  '& img': {
    objectFit: 'contain',
    objectPosition: `bottom ${placement}`,
    opacity: FEATURE_OPACITY,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundImage: featureScrim(placement),
  },
});

/**
 * `onDarkBand` is the band's tone, not the page's colour scheme, and the two
 * disagree on exactly one band: an `inverse` section is near-black in light
 * mode too. A light cut-out on that surface is the carrying direction of the
 * pair, so it takes the quieter value in both schemes — and, having no scheme
 * to switch on, it is handed back without the override, one fewer rule in the
 * stylesheet for the band that cannot use it.
 */
export const sectionBackdropSx = (
  placement: SectionBackdropPlacement,
  onDarkBand = false,
  emphasis: SectionBackdropEmphasis = 'watermark',
): BackdropSx[] => {
  // `feature` never branches on `onDarkBand`: it is dark-band-only by
  // contract, so the only thing left to vary is which near-black the band is
  // in this scheme, and both arms of that answer are the scrim's colour.
  if (emphasis === 'feature') {
    return [
      featureLayer(placement) as BackdropSx,
      (theme) => theme.applyStyles('dark', BAND_DARK) as SystemStyleObject<Theme>,
    ];
  }

  const mask = cornerMask(placement);

  const layer = {
    ...OPACITY_LIGHT,
    position: 'absolute',
    insetBlock: 0,
    ...containerAnchor(placement, 720),
    // Narrow enough on a desktop to stay in the margin beside a two-column
    // band; wider on a phone, where the car has the full width to itself and
    // sits under the foot of a single stacked column.
    width: { xs: '88%', sm: '64%', md: '46%' },
    maxWidth: 720,
    opacity: 'var(--kabex-backdrop-opacity)',
    // Texture, not photography: a grey silhouette reads as a watermark,
    // while a tinted one at 11% reads as a colour cast on the band.
    filter: 'grayscale(1)',
    pointerEvents: 'none',
    // Against the container's `1`, so the layer needs no negative index and
    // no isolation of its own — the same stacking contract `SectionPattern`
    // signs.
    zIndex: 0,
    maskImage: mask,
    WebkitMaskImage: mask,
    '& picture, & img': { display: 'block', width: '100%', height: '100%' },
    // `contain` and not `cover`: the car is already trimmed to its own
    // bounds, so it is scaled by whichever of the band's two dimensions
    // binds first and is never cropped or stretched.
    '& img': { objectFit: 'contain', objectPosition: `bottom ${placement}` },
  };

  if (onDarkBand) return [{ ...layer, ...OPACITY_DARK }];

  return [layer, (theme) => theme.applyStyles('dark', OPACITY_DARK) as SystemStyleObject<Theme>];
};
